import { useRef, useCallback } from "react";

/**
 * Returns a debounced version of `fn` that delays invocation by `delay` ms.
 * The timer is stored in a ref — each component instance gets its own timer,
 * so multiple instances never clobber each other (unlike a module-level variable).
 *
 * The returned function is stable across renders (wrapped in useCallback with
 * no deps), so it is safe to use as an event handler prop without causing
 * unnecessary re-renders.
 *
 * @param {Function} fn    - The function to debounce.
 * @param {number}   delay - Delay in milliseconds (default 300).
 * @returns {Function} Debounced function. Call `.cancel()` to clear a pending timer.
 */
function useDebouncedCallback(fn, delay = 300) {
  const timerRef = useRef(null);
  const fnRef = useRef(fn);
  fnRef.current = fn; // always call the latest version of fn

  const debounced = useCallback(
    (...args) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        fnRef.current(...args);
      }, delay);
    },
    [delay]
  );

  debounced.cancel = () => clearTimeout(timerRef.current);

  return debounced;
}

export default useDebouncedCallback;
